import { assertValidTicketPayment, calculateDiscountForLines } from '../../../lib/discounts.ts'
import { createId, getLineTotal, getTicketTotal } from '../../../lib/format.ts'
import { calculateTaxFromGross, isValidTaxRate } from '../../../lib/tax.ts'
import type {
  AppliedDiscount,
  CashSession,
  Customer,
  PaymentMethod,
  SaleCreatedPayload,
  SaleLinePayload,
  TenantContext,
  TicketLine,
} from '../../../types/index.ts'
import { customerFiscalSnapshot } from '../../customers/customerValidation.ts'
import { nowIso } from '../../../utils/dates.ts'

function buildTicketPayload(
  context: TenantContext,
  cashSession: CashSession,
  lines: TicketLine[],
  paymentMethod: PaymentMethod | null,
  receivedCents: number | null,
  discount: AppliedDiscount | null,
  validatePayment: boolean,
  invoiceCustomer: Customer | null,
  identity?: { saleId?: string },
): SaleCreatedPayload {
  const createdAt = nowIso()
  const ticketId = createId()
  const saleId = identity?.saleId ?? createId()
  const subtotalCents = getTicketTotal(lines)
  const grossLineTotals = lines.map(getLineTotal)
  const calculation = calculateDiscountForLines(
    lines.map((line, index) => ({ ...line, grossCents: grossLineTotals[index] })),
    discount,
  )
  const { discountAmountCents, totalCents } = calculation
  if (validatePayment) assertValidTicketPayment(totalCents, paymentMethod)
  const saleLines: SaleLinePayload[] = lines.map((line, index) => {
    const taxRate = line.fiscalSnapshot?.taxRate
      ?? line.catalogSnapshot.vatRate
      ?? context.venueDefaultTaxRate
    const fiscalSnapshot = taxRate !== undefined && isValidTaxRate(taxRate)
      ? { taxRate, ...calculateTaxFromGross(calculation.lineAllocations[index].netCents, taxRate) }
      : null

    return {
      id: createId(),
      ticketId,
      tenantId: context.tenantId,
      productId: line.productId,
      variantId: line.variantId,
      productName: line.productName,
      variantName: line.variantName,
      basePriceCents: line.basePriceCents,
      componentDeltaCents: line.componentDeltaCents,
      modifierDeltaCents: line.modifierDeltaCents,
      grossBeforeDiscountCents: line.unitPriceCents,
      quantity: line.quantity,
      unitPriceCents: line.unitPriceCents,
      lineTotalCents: grossLineTotals[index],
      discountAmountCents: calculation.lineAllocations[index].discountAmountCents,
      netTotalCents: calculation.lineAllocations[index].netCents,
      modifiers: line.modifiers,
      components: line.components,
      catalogSnapshot: line.catalogSnapshot,
      fiscalSnapshot,
    }
  })
  const discountSnapshot: AppliedDiscount | null = discount ? {
    ...discount,
    economicSource: 'pos_closed_sale',
    economicSnapshotVersion: 1,
    storedValue: discount.calculationType === 'fixed' ? discount.value / 100 : discount.value,
    calculationLines: lines.map((line, index) => ({
      productId: line.productId,
      variantId: line.variantId || null,
      grossCents: grossLineTotals[index],
      quantity: line.quantity,
    })),
    eligibleSubtotalCents: calculation.eligibleSubtotalCents,
    discountAmountCents,
    amountCents: discountAmountCents,
    totalCents,
    lineAllocations: calculation.lineAllocations.map((allocation, index) => ({
      index,
      lineId: saleLines[index].id,
      productId: lines[index].productId,
      variantId: lines[index].variantId || null,
      quantity: lines[index].quantity,
      ...allocation,
    })),
  } : null

  return {
    ticket: {
      id: ticketId,
      tenantId: context.tenantId,
      cashSessionId: cashSession.id,
      cashRegisterId: cashSession.cashRegisterId,
      venueId: context.venueId,
      deviceId: context.deviceId,
      userId: context.userId,
      subtotalCents,
      discount: discountSnapshot,
      discountAmountCents,
      totalCents,
      createdAt,
      invoice: invoiceCustomer ? {
        customerId: invoiceCustomer.id,
        customer: customerFiscalSnapshot(invoiceCustomer),
        series: null,
        number: null,
        issuedAt: null,
      } : null,
    },
    lines: saleLines,
    sale: {
      id: saleId,
      tenantId: context.tenantId,
      ticketId,
      cashSessionId: cashSession.id,
      cashRegisterId: cashSession.cashRegisterId,
      venueId: context.venueId,
      deviceId: context.deviceId,
      userId: context.userId,
      totalCents,
      paymentMethod,
      createdAt,
    },
    payment: paymentMethod ? {
      id: createId(),
      tenantId: context.tenantId,
      saleId,
      method: paymentMethod,
      amountCents: totalCents,
      receivedCents,
      changeCents: Math.max(0, (receivedCents ?? totalCents) - totalCents),
    } : null,
  }
}

export function buildSalePayload(
  context: TenantContext,
  cashSession: CashSession,
  lines: TicketLine[],
  paymentMethod: PaymentMethod | null,
  receivedCents: number | null,
  discount: AppliedDiscount | null,
  invoiceCustomer: Customer | null = null,
  identity?: { saleId?: string },
) {
  return buildTicketPayload(context, cashSession, lines, paymentMethod, receivedCents, discount, true, invoiceCustomer, identity)
}

export function buildPreTicketPayload(
  context: TenantContext,
  cashSession: CashSession,
  lines: TicketLine[],
  discount: AppliedDiscount | null,
  invoiceCustomer: Customer | null = null,
) {
  return buildTicketPayload(context, cashSession, lines, null, null, discount, false, invoiceCustomer)
}
